<script setup>
import { $api, getApiBaseUrl } from "@/utils/api";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const contratos = ref([]);
const total = ref(0);
const loading = ref(false);
const search = ref("");
const statusFilter = ref("");
const filtroObra = ref("");
const dataInicial = ref("");
const dataFinal = ref("");
const page = ref(1);
const perPage = ref(15);
const options = ref({ page: 1, itemsPerPage: 15 });

const statusOptions = [
  { title: "Todos", value: "" },
  { title: "Ativo", value: "ATIVO" },
  { title: "Encerrado", value: "ENCERRADO" },
];

async function fetchContratos() {
  loading.value = true;
  try {
    const params = new URLSearchParams({
      page: String(options.value.page),
      per_page: String(options.value.itemsPerPage),
    });
    if (statusFilter.value) params.set("status", statusFilter.value);
    if (filtroObra.value.trim()) params.set("obra", filtroObra.value.trim());
    if (dataInicial.value) params.set("data_inicial", dataInicial.value);
    if (dataFinal.value) params.set("data_final", dataFinal.value);
    if (search.value.trim()) params.set("search", search.value.trim());
    const data = await $api(`/contratos-prestadores?${params}`);
    contratos.value = data.data ?? [];
    total.value = data.total ?? 0;
  } catch (e) {
    console.error(e);
    contratos.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

function onOptionsChange(opts) {
  options.value = opts;
  fetchContratos();
}

watch([search, statusFilter, filtroObra, dataInicial, dataFinal], () => {
  options.value.page = 1;
  fetchContratos();
});

// Garante que data inicial não seja maior que data final
watch(dataInicial, (nova) => {
  if (nova && dataFinal.value && nova > dataFinal.value) dataFinal.value = "";
});
watch(dataFinal, (nova) => {
  if (nova && dataInicial.value && dataInicial.value > nova) dataInicial.value = "";
});

function limparFiltros() {
  search.value = "";
  statusFilter.value = "";
  filtroObra.value = "";
  dataInicial.value = "";
  dataFinal.value = "";
  options.value.page = 1;
  fetchContratos();
}

const temFiltroAtivo = computed(() =>
  !!(
    search.value?.trim() ||
    statusFilter.value ||
    filtroObra.value?.trim() ||
    dataInicial.value ||
    dataFinal.value
  )
);

const pdfError = ref("");
const pdfSnackbar = ref(false);

// Dialog Enviar Anexo
const dialogAnexo = ref(false);
const anexoEnviando = ref(false);
const anexoErro = ref("");
const formAnexo = ref({
  numero_contrato: "",
  obra: "",
  prestador_id: null,
  anexo: null,
});
const obrasOptions = ref([]);
const prestadoresOptions = ref([]);
const prestadoresCarregando = ref(false);

async function abrirDialogAnexo() {
  dialogAnexo.value = true;
  anexoErro.value = "";
  formAnexo.value = { numero_contrato: "", obra: "", prestador_id: null, anexo: null };
  try {
    const [obras, dataPrestadores] = await Promise.all([
      $api("/contratos-prestadores/obras"),
      $api("/prestadores?per_page=500"),
    ]);
    obrasOptions.value = Array.isArray(obras) ? obras : [];
    prestadoresOptions.value = dataPrestadores?.data ?? [];
  } catch (e) {
    console.error(e);
    obrasOptions.value = [];
    prestadoresOptions.value = [];
  }
}

function fecharDialogAnexo() {
  dialogAnexo.value = false;
}

/** Extrai o objeto File do valor do VFileInput (pode ser array ou objeto). */
function extrairArquivo(val) {
  if (!val) return null;
  if (val instanceof File) return val;
  const arr = Array.isArray(val) ? val : [val];
  for (const item of arr) {
    if (item instanceof File) return item;
    if (item?.file instanceof File) return item.file;
    if (item?.raw instanceof File) return item.raw;
  }
  return null;
}

async function enviarAnexo() {
  if (!formAnexo.value.numero_contrato?.trim()) {
    anexoErro.value = "Informe o número do contrato.";
    return;
  }
  if (!formAnexo.value.obra?.trim()) {
    anexoErro.value = "Informe ou selecione a obra.";
    return;
  }
  if (!formAnexo.value.prestador_id) {
    anexoErro.value = "Selecione o prestador.";
    return;
  }
  const file = extrairArquivo(formAnexo.value.anexo);
  if (!file) {
    anexoErro.value = "Anexe um arquivo (PDF, Word, planilha ou imagem).";
    return;
  }
  anexoErro.value = "";
  anexoEnviando.value = true;
  try {
    const token = useCookie("accessToken").value;
    const apiBase = getApiBaseUrl();
    const fd = new FormData();
    fd.append("numero_contrato", formAnexo.value.numero_contrato.trim());
    fd.append("obra", formAnexo.value.obra.trim());
    fd.append("prestador_id", String(formAnexo.value.prestador_id));
    fd.append("anexo", file, file.name || "anexo");
    const res = await fetch(`${apiBase}/contratos-prestadores/anexo`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.message;
      const errs = data?.errors;
      const lista = errs ? Object.values(errs).flat().filter(Boolean) : [];
      anexoErro.value = msg || (lista.length ? lista.join(" ") : "Falha ao enviar anexo.");
      return;
    }
    fecharDialogAnexo();
    fetchContratos();
  } catch (e) {
    console.error(e);
    anexoErro.value = e?.message || "Erro de rede. Verifique a conexão e tente novamente.";
  } finally {
    anexoEnviando.value = false;
  }
}

async function abrirPdf(id) {
  pdfError.value = "";
  pdfSnackbar.value = false;
  try {
    const token = useCookie("accessToken").value;
    const apiBase = getApiBaseUrl();
    const res = await fetch(`${apiBase}/contratos-prestadores/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const contentType = res.headers.get("content-type") || "";
    if (!res.ok) {
      let msg = "Falha ao gerar PDF.";
      if (contentType.includes("application/json")) {
        try {
          const data = await res.json();
          msg = data?.error || data?.message || msg;
        } catch (_) {}
      }
      pdfError.value = msg;
      pdfSnackbar.value = true;
      return;
    }
    if (contentType.includes("application/pdf")) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } else {
      pdfError.value = "Resposta inválida do servidor.";
      pdfSnackbar.value = true;
    }
  } catch (e) {
    console.error(e);
    pdfError.value = e?.message || "Erro ao gerar PDF. Verifique a conexão.";
    pdfSnackbar.value = true;
  }
}

onMounted(() => fetchContratos());
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardTitle class="text-h5 pa-4 d-flex align-center">
            <span>Contratos de Prestadores</span>
            <VSpacer />
            <VBtn
            variant="outlined"
            size="small"
            color="secondary"
            @click="abrirDialogAnexo"
          >
            <VIcon icon="tabler-upload" start />
            Enviar Anexo
          </VBtn>
            <VBtn color="primary" size="small" :to="{ name: 'contratos-novo' }">
              <VIcon icon="tabler-plus" start />
              Novo Contrato
            </VBtn>
          </VCardTitle>
          <VDivider />
          <VCardText class="pa-4">
            <!-- Filtros -->
            <div class="d-flex flex-wrap align-end gap-3 mb-4">
              <VTextField
                v-model="search"
                label="Buscar"
                placeholder="Nº contrato, nome ou CPF/CNPJ"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 200px; max-width: 280px;"
                prepend-inner-icon="tabler-search"
                variant="outlined"
              />
              <VTextField
                v-model="filtroObra"
                label="Obra"
                placeholder="Nome da obra do contrato"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 180px; max-width: 220px;"
                prepend-inner-icon="tabler-building"
                variant="outlined"
              />
              <VSelect
                v-model="statusFilter"
                label="Status"
                :items="statusOptions"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 130px; max-width: 160px;"
                prepend-inner-icon="tabler-circle-dot"
                variant="outlined"
              />
              <VTextField
                v-model="dataInicial"
                label="Data inicial"
                type="date"
                :max="dataFinal || undefined"
                placeholder="Início do período"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 150px; max-width: 160px;"
                variant="outlined"
              />
              <VTextField
                v-model="dataFinal"
                label="Data final"
                type="date"
                :min="dataInicial || undefined"
                placeholder="Fim do período"
                density="compact"
                hide-details
                clearable
                class="flex-grow-1 flex-md-grow-0"
                style="min-width: 150px; max-width: 160px;"
                variant="outlined"
              />
              <VTooltip text="Limpar todos os filtros" location="bottom">
                <template #activator="{ props: tProps }">
                  <VBtn
                    v-bind="tProps"
                    icon
                    variant="outlined"
                    size="small"
                    color="secondary"
                    :disabled="!temFiltroAtivo"
                    @click="limparFiltros"
                  >
                    <VIcon icon="tabler-filter-off" />
                  </VBtn>
                </template>
              </VTooltip>
            </div>

            <!-- Tabela -->
            <VDataTableServer
            v-model:options="options"
            :headers="[
              { title: 'Nº Contrato', key: 'numero_contrato', sortable: false },
              { title: 'Obra / Projeto', key: 'obra', sortable: false },
              {
                title: 'Nome / Razão Social',
                key: 'nome_razao_social',
                sortable: false,
              },
              { title: 'CPF/CNPJ', key: 'cpf_cnpj', sortable: false },
              { title: 'Função', key: 'funcao', sortable: false },
              { title: 'Status', key: 'status', sortable: false },
              { title: 'Origem', key: 'origem', sortable: false, width: 100 },
              { title: 'Ações', key: 'actions', sortable: false, width: 200 },
            ]"
            :items="contratos"
            :items-length="total"
            :loading="loading"
            loading-text="Carregando..."
            @update:options="onOptionsChange">
            <template #item.obra="{ item }">
              {{ item.obra || '—' }}
            </template>
            <template #item.origem="{ item }">
              <VChip
                :color="(item.origem || 'sistema') === 'anexo' ? 'secondary' : 'default'"
                size="small"
                variant="tonal"
              >
                {{ (item.origem || 'sistema') === 'anexo' ? 'Anexo' : 'Sistema' }}
              </VChip>
            </template>
            <template #item.status="{ item }">
              <VChip
                :color="item.status === 'ATIVO' ? 'success' : 'default'"
                size="small">
                {{ item.status }}
              </VChip>
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex gap-1">
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  :to="{ name: 'contratos-id', params: { id: item.id } }">
                  <VIcon icon="tabler-eye" />
                </VBtn>
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  :to="{
                    name: 'contratos-id',
                    params: { id: item.id },
                    query: { edit: '1' },
                  }">
                  <VIcon icon="tabler-edit" />
                </VBtn>
                <VBtn
                  icon
                  variant="text"
                  size="small"
                  @click="abrirPdf(item.id)">
                  <VIcon icon="tabler-file-export" />
                </VBtn>
              </div>
            </template>
          </VDataTableServer>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Dialog Enviar Anexo -->
    <VDialog
      v-model="dialogAnexo"
      max-width="500"
      persistent
      @click:outside="fecharDialogAnexo"
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-upload" class="me-2" />
          Enviar Anexo de Contrato
        </VCardTitle>
        <VDivider />
        <VCardText>
          <p class="text-body-2 text-medium-emphasis mb-4">
            Cadastre um contrato já existente informando número, obra, prestador e anexando o arquivo.
          </p>
          <VForm @submit.prevent="enviarAnexo">
            <VTextField
              v-model="formAnexo.numero_contrato"
              label="Número do contrato"
              placeholder="Ex.: 2024/001"
              density="compact"
              variant="outlined"
              class="mb-3"
              clearable
            />
            <VAutocomplete
              v-model="formAnexo.obra"
              :items="obrasOptions"
              label="Obra / Projeto"
              placeholder="Selecione ou digite a obra"
              density="compact"
              variant="outlined"
              class="mb-3"
              clearable
              no-filter
            />
            <VAutocomplete
              v-model="formAnexo.prestador_id"
              :items="prestadoresOptions"
              item-title="nome_razao_social"
              item-value="id"
              label="Prestador"
              placeholder="Selecione o prestador"
              density="compact"
              variant="outlined"
              class="mb-3"
              clearable
            />
            <VFileInput
              v-model="formAnexo.anexo"
              label="Arquivo do contrato"
              placeholder="Clique ou arraste o arquivo aqui"
              density="compact"
              variant="outlined"
              class="mb-3"
              :multiple="false"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.xls,.xlsx,.odt,.ods,.txt,.rtf,.csv"
              prepend-icon=""
              prepend-inner-icon="tabler-paperclip"
              show-size
            />
            <VAlert
              v-if="anexoErro"
              type="error"
              density="compact"
              class="mb-3"
              closable
            >
              {{ anexoErro }}
            </VAlert>
            <div class="d-flex justify-end gap-2">
              <VBtn variant="outlined" @click="fecharDialogAnexo">
                Cancelar
              </VBtn>
              <VBtn
                type="submit"
                color="primary"
                :loading="anexoEnviando"
                :disabled="anexoEnviando"
              >
                Enviar Anexo
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="pdfSnackbar"
      color="error"
      location="top"
      :timeout="8000">
      {{ pdfError }}
      <template #actions>
        <VBtn variant="text" size="small" @click="pdfSnackbar = false">
          Fechar
        </VBtn>
      </template>
    </VSnackbar>
  </div>
</template>
